/**
 * PURPOSE: Descargar el PDF de resultados de la fecha/etapa seleccionada + tabla de posiciones (FTR-0060).
 * RESPONSIBILITIES:
 * - Sí: GET /resultados-fecha-pdf con el token de staff, nombre de archivo desde Content-Disposition,
 *   mensaje de error del servidor (el JSON llega como Blob) y aviso si la tabla de POSICIONES está desactualizada.
 * - No: no decide qué fecha/fase; las recibe de ResultadosArea (las mismas que muestran los chips).
 * COLLABORATORS: ResultadosArea.jsx, InstitucionesArea/sportReportsService.js
 *   (triggerBlobDownload), FullScreenPlanilla/shared/fedupPreview.utils.js (readBlobAsText).
 * NOTES: usa el axios global, como el resto de ResultadosArea: src/index.js le registra los mismos interceptores
 *   Bearer/401 y de compañía que httpService (FTR-0019 / FTR-0012).
 * ROUTE: src/area/ResultadosArea/resultadosFechaPdfService.js
 */
import axios from "axios";

import API_BASE_URL from "../../config/config.js";
import { triggerBlobDownload } from "../InstitucionesArea/sportReportsService.js";
import { readBlobAsText } from "../../components/FullScreenModal/FullScreenPlanilla/shared/fedupPreview.utils.js";

export const RESULTADOS_PDF_ERROR = "No se pudo generar el PDF de resultados.";
export const STANDINGS_STALE_WARNING =
  "La tabla de POSICIONES de Winscore no está actualizada: el PDF usa el cálculo actual. Actualiza las posiciones para que coincidan.";

/** "attachment; filename=Resultados_X_Fecha1.pdf" -> "Resultados_X_Fecha1.pdf" */
export function filenameFromDisposition(disposition, fallback) {
  const match = /filename\*?=(?:UTF-8'')?"?([^";\n]+)"?/i.exec(String(disposition || ""));
  if (!match) return fallback;
  try {
    return decodeURIComponent(match[1].trim()) || fallback;
  } catch {
    return match[1].trim() || fallback;
  }
}

/** Mensaje legible para el toast: `msg` del servidor si llegó, si no el genérico. */
export async function resultadosPdfErrorMessage(error) {
  const data = error?.response?.data;
  if (!data) return RESULTADOS_PDF_ERROR;
  try {
    const text = typeof data === "string" ? data : await readBlobAsText(data);
    const msg = String(JSON.parse(text)?.msg || "").trim();
    return msg || RESULTADOS_PDF_ERROR;
  } catch {
    return RESULTADOS_PDF_ERROR;
  }
}

/**
 * @param {{ idevent: string|number, idsport: string|number, idtypephase: 1|2, nroFecha: number }} params
 * @returns {Promise<{ blob: Blob, filename: string, standingsStale: boolean }>}
 */
export async function fetchResultadosFechaPdf({ idevent, idsport, idtypephase, nroFecha }) {
  const resp = await axios.get(`${API_BASE_URL}/resultados-fecha-pdf`, {
    params: { idevent, idsport, idtypephase, nro_fecha: nroFecha },
    responseType: "blob",
    headers: { Accept: "application/pdf" },
  });
  const headers = resp.headers || {};
  return {
    blob: resp.data,
    filename: filenameFromDisposition(headers["content-disposition"], `Resultados_Fecha${nroFecha}.pdf`),
    standingsStale: String(headers["x-standings-sync"] || "").toLowerCase() === "stale",
  };
}

/** Descarga y guarda el archivo. Devuelve si la tabla de POSICIONES estaba desactualizada. */
export async function downloadResultadosFechaPdf(params) {
  const { blob, filename, standingsStale } = await fetchResultadosFechaPdf(params);
  triggerBlobDownload(blob, filename);
  return { filename, standingsStale };
}
